import fetch from 'libs/fetchWithAuth';
import { apiUrl } from 'libs/apiUrl';

const _baseUrl = apiUrl('/api/ext/tasks');

class Tasks {
	static getList(project, service, req) {
		return fetch(_baseUrl + `/project/${project}/step/${service}`, {}, req);
	}

	static getAllProjectTasks(project, req) {
		return fetch(_baseUrl + `/project/${project}/allTasks`, {}, req);
	}

	// Per-task start/end window (dateCreated/dueDate) plus dateEmailed/
	// dateAccepted/dateFinished for every task in a project - a leaner,
	// general-purpose read for anything that needs task scheduling data
	// without pulling the full allTasks payload (subtasks, mtEngines, etc).
	static getProjectTimeline(project, req) {
		return fetch(_baseUrl + `/project/${project}/timeline`, {}, req);
	}

	static get(id, req) {
		return fetch(_baseUrl + '/' + id, {}, req);
	}

	static getSourceComparisonStats(hash, viewID, req) {
		const viewQuery = viewID ? `?viewID=${viewID}` : '';
		return fetch(_baseUrl + `/${hash}/source-comparison-stats${viewQuery}`, {}, req);
	}

	static runQASample(taskId, req)
	{
		return fetch(
			_baseUrl + '/run-qa-sample',
			{
				method: 'POST',
				body: JSON.stringify({
					taskID: taskId
				}),
			},
			req
		);
	}

	static sendCode(taskId, req)
	{
		return fetch(
			_baseUrl + '/send-code',
			{
				method: 'POST',
				body: JSON.stringify({
					taskID: taskId
				}),
			},
			req
		);
	}

	static codeIsValid(email, code, req)
	{
		return fetch(
			_baseUrl + '/code-is-valid',
			{
				method: 'POST',
				body: JSON.stringify({
					email: email,
					code: code
				}),
			},
			req
		);
	}

	static getEmailFrom(req)
	{
		return fetch(
			_baseUrl + '/email-from',
			{
				method: 'GET'
			},
			req
		);
	}

	static getForEditor(id, req) {
		return fetch(`${_baseUrl}/get-for-editor/${id}`, {}, req);
	}

	// Requests cancellation of whichever pretranslate flow (AI persona, TM, MT,
	// pseudo, or the combined one) is currently running against this task. It's
	// cooperative, not instant - the in-flight batch/segment finishes first, then
	// the running flow stops and isPretranslating flips back to 0, which the
	// existing polling on the tasks grid already picks up.
	static cancelPretranslate(id, req) {
		return fetch(
			`${_baseUrl}/${id}/cancel-pretranslate`,
			{
				method: 'POST'
			},
			req
		);
	}

	static getByHash(hash, req) {
		return fetch(_baseUrl + '/get-by-hash/' + hash, {}, req);
	}

	// Segment/word totals for the thank-you page (see
	// Tasks::getCompletionStats() on the backend).
	static getCompletionStats(id, req) {
		return fetch(_baseUrl + '/' + id + '/completion-stats', {}, req);
	}

	static getFromHashWithoutAuth(hash, req) {
		return fetch(_baseUrl + '/get-by-hash-wihout-auth/' + hash, {}, req);
	}

	static updateLightByHash(hash, req) {
		return fetch(_baseUrl + '/update-light-by-hash/' + hash, {}, req);
	}

	static getMultipleByHash(hashArray, req) {
		return fetch(_baseUrl + '/get-multiple-by-hash/', hashArray, req);
	}

	static areAllSegmentsTranslated(id, req) {
		return fetch(_baseUrl + '/all-segments-translated/' + id, {}, req);
	}

	static areAllSegmentsTranslatedMultiple(data, req) {
		return fetch(
			_baseUrl + '/all-segments-translated-multiple',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static delete(data, req) {
		return fetch(
			_baseUrl + '/delete',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static sendEmail(data, req) {
		return fetch(
			_baseUrl + '/send-email',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static assignUser(data, req) {
		return fetch(
			_baseUrl + '/assign-user',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static assignClient(data, req) {
		return fetch(
			_baseUrl + '/assign-client',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static updateStatus(data, req) {
		return fetch(
			_baseUrl + '/update-status',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static getJobRating(taskID, req, completionTaskID = 0) {
		return fetch(_baseUrl + `/rate-job/${taskID}?completionTaskID=${completionTaskID}`, {}, req);
	}

	static getCompletionRating(taskIDs, { linguistView = false } = {}, req) {
		return fetch(_baseUrl + '/completion-rating', {
			method: 'POST',
			body: JSON.stringify({ taskIDs, linguistView }),
		}, req);
	}

	static rateJob(data, req) {
		return fetch(
			_baseUrl + '/rate-job',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static setHashPublic(data, req) {
		return fetch(
			_baseUrl + '/set-hash-public',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static updateDueDate(data, req) {
		return fetch(
			_baseUrl + '/update-due-date',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static updateDueDateMultiple(data, req) {
		return fetch(
			_baseUrl + '/update-due-date-multiple',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static save(data, req) {
		let _url = _baseUrl;
		let _method = 'POST';

		if (data.id != '0') {
			_url = _baseUrl + '/' + data.id;
			_method = 'PUT';
		}

		return fetch(
			_url,
			{
				method: _method,
				body: JSON.stringify(data),
			},
			req
		);
	}

	static accept(data, req) {
		return fetch(
			_baseUrl + '/accept',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static decline(data, req) {
		return fetch(
			_baseUrl + '/decline',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static finish(data, req) {
		return fetch(
			_baseUrl + '/finish',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static finishMultiple(data, req) {
		return fetch(
			_baseUrl + '/finish-multiple',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static uploadBilingual(data, req) {
		return fetch(
			_baseUrl + '/upload-bilingual',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static previewTargetFile(data, req) {
		return fetch(_baseUrl + '/preview-target-file', { method: 'POST', body: JSON.stringify(data) }, req);
	}

	static importTargetFile(data, req) {
		return fetch(_baseUrl + '/import-target-file', { method: 'POST', body: JSON.stringify(data) }, req);
	}

	static checkBilingual(data, req) {
		return fetch(
			_baseUrl + '/check-bilingual',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static getSegmentsMinimal(data, req) {
		return fetch(
			_baseUrl + '/get-segments-minimal',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static split(data, req) {
		return fetch(
			_baseUrl + '/split',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static reorderAndRemoveParent(data, req) {
		return fetch(
			_baseUrl + '/reorder-and-remove-parent',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static getAllStepsByTask(data, req) {
		return fetch(
			_baseUrl + '/get-all-step-by-task',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static deleteAllSubtasks(data, req) {
		return fetch(
			_baseUrl + '/delete-all-subtasks',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static getProgress(id, req) {
		return fetch(_baseUrl + '/get-progress/' + id, {}, req);
	}

	static getDeleted(projectID, req) {
		return fetch(_baseUrl + `/project/${projectID}/deleted`, {}, req);
	}

	static restore(data, req) {
		return fetch(
			_baseUrl + '/restore',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static flag(data, req) {
		return fetch(
			_baseUrl + '/update-flag',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static relock(data, req) {
		return fetch(
			_baseUrl + '/update-lock',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static pin(data, req) {
		return fetch(
			_baseUrl + '/update-pin',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static updateTranslatorNotes(data, req) {
		return fetch(
			_baseUrl + '/update-translator-notes',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static updateTMs(data, req) {
		return fetch(
			_baseUrl + '/update-tms',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static updateSource(data, req) {
		return fetch(
			_baseUrl + '/update-source',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static getSourceUpdateStatus(id, req) {
		return fetch(`${_baseUrl}/source-update-status/${id}`, {}, req);
	}

	static getSourceUpdateStatusByTask(data, req) {
		return fetch(
			_baseUrl + '/source-update-status',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static acknowledgeSourceUpdate(taskID, req) {
		return fetch(
			_baseUrl + '/source-update-acknowledge',
			{
				method: 'POST',
				body: JSON.stringify({ taskID }),
			},
			req
		);
	}

	static findUnstoredSegments(id, req) {
		return fetch(`${_baseUrl}/find-unstored-segments/${id}`, {}, req);
	}

	static updatePreferences(id, preferenceKey, req) {
		return fetch(`${_baseUrl}/update-prefs/${id}/${preferenceKey}`, {}, req);
	}

	static areAllSegmentsConfirmed(id, req) {
		return fetch(_baseUrl + '/are-all-confirmed/' + id, {}, req);
	}

	static requestReopen(data, req) {
		return fetch(
			_baseUrl + '/request-reopen',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static reopen(data, req) {
		return fetch(
			_baseUrl + '/reopen',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static getCompletedViewData(data, req) {
		return fetch(
			_baseUrl + '/completed-view-data',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static editedSegments(id, req) {
		return fetch(`${_baseUrl}/edited-segments/${id}`, {}, req);
	}

	static completeTask(taskId, req) {
		return fetch(
			`${_baseUrl}/${taskId}/complete-task`,
			{
				method: 'POST'
			},
			req
		);
	}

	static getDueTasks(req) {
		return fetch(`${_baseUrl}/due-tasks`, {}, req);
	}

	static getOverDueTasks(req) {
		return fetch(`${_baseUrl}/overdue-tasks`, {}, req);
	}
	static getTomorrowTasks(req) {
		return fetch(`${_baseUrl}/tomorrow-tasks`, {}, req);
	}

	static getNextReadyForDelivery(req) {
		return fetch(`${_baseUrl}/next-ready-for-delivery`, {}, req);
	}

	static getActiveTasks(req) {
		return fetch(`${_baseUrl}/active`, {}, req);
	}

	static getPrevNextStepByTaskId(taskId, req) {
		return fetch(`${_baseUrl}/prev-next-step/${taskId}`, {}, req);
	}

	static getAllTasks(data, req) {
		return fetch(
			'/api/ext/tasks/all-tasks',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static getSegmentsTask(taskId, req) {
		return fetch(`${_baseUrl}/get-segments-task/${taskId}`, {}, req);
	}

	static splitBySegment(data, req) {
		return fetch(
			'/api/ext/tasks/split-by-segment',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static lockSegments(data, req) {
		return fetch(
			'/api/ext/tasks/lock-segments',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}

	static unlockSegments(data, req) {
		return fetch(
			'/api/ext/tasks/unlock-segments',
			{
				method: 'POST',
				body: JSON.stringify(data),
			},
			req
		);
	}
}

export default Tasks;
